<template>
    <UFieldGroup class="flex flex-col gap-1 bg-gray-100">
        <year-matrix v-model="innerRange" :is-disabled="yearDisabled">
            <template #default="slotProps">
                <vat-period-select :range="slotProps.year" :status="findStatus"/>
            </template>
        </year-matrix>
        <term-matrix v-if="!vatAnnually" v-model="innerRange" :is-disabled="termDisabled">
            <template #default="slotProps">
                <vat-period-select :range="slotProps.term" :status="findStatus"/>
            </template>
        </term-matrix>
    </UFieldGroup>
</template>

<script setup lang="ts">

import YearMatrix from '@/dateUtils/v2/YearMatrix.vue'
import TermMatrix from '@/dateUtils/v2/TermMatrix.vue'
import {DateRange} from '@/dateUtils/DateRange'
import {VatPeriodStatus} from '@/vat/vatReport.service'
import {toIsoDate} from '@/dateUtils/dateUtils'
import {computed} from 'vue'
import VatPeriodSelect from '@/vat/VatPeriodSelect.vue'

const range = defineModel<DateRange>({required: true})

const props = defineProps({
    vatPeriodStatuses: {
        type: Object as () => VatPeriodStatus[],
        required: false,
        default: Object as () => []
    },
    vatAnnually: {
        type: Boolean,
        required: false,
        default: false
    }
})

const innerRange = computed({
    get: () => range.value,
    set: (newValue: DateRange) => {
        let rangeToSet = newValue
        if (!props.vatAnnually && newValue.end.getMonth() - newValue.start.getMonth() > 2) {
            rangeToSet = findInnerRange(newValue) ?? newValue
            if (!innerRange.value) return
        }
        range.value = rangeToSet
    }
})

const yearDisabled = (range: DateRange): boolean => {
    const start = toIsoDate(range.start)
    const end = toIsoDate(range.end)
    return !props.vatPeriodStatuses.some(vps => start <= vps.startDate && vps.endDate <= end)
}

const findInnerRange = (range: DateRange): DateRange | undefined => {
    const start = toIsoDate(range.start)
    const end = toIsoDate(range.end)
    const status = props.vatPeriodStatuses.find((vps => start <= vps.startDate && vps.endDate <= end))
    if (status) {
        return {
            start: new Date(status.startDate),
            end: new Date(status.endDate)
        }
    }
}

const findStatus = (range: DateRange): 'DueReporting' | 'Completed' | undefined => {
    const start = toIsoDate(range.start)
    const end = toIsoDate(range.end)
    let maxStatus: 'DueReporting' | 'Completed' | undefined = undefined
    props.vatPeriodStatuses.forEach(vps => {
        if (start <= vps.startDate && vps.endDate <= end) {
            if (!maxStatus) {
                maxStatus = vps.status
            } else if (maxStatus === 'Completed') {
                maxStatus = vps.status
            }
        }
    })
    return maxStatus
}

const termDisabled = (range: DateRange): boolean => {
    const start = toIsoDate(range.start)
    const end = toIsoDate(range.end)
    return !props.vatPeriodStatuses.some(vps => vps.startDate === start && vps.endDate === end)
}


</script>

<style scoped>

</style>